import {
  EDITOR_MAX_BLOCKS_PER_BOX,
  EDITOR_MAX_RUN_CHARACTERS,
  EDITOR_MAX_RUNS_PER_BLOCK,
  isEditorTextBoxPlaceholderOnly,
  validateEditorFieldParams,
} from '@messenger/lotus'
import type {
  EditorCharacterStylingSpec,
  EditorTextBoxInput,
} from '@messenger/lotus/row-store'
import { canonicalFontName } from '../fontNames'
import { tinyMceBlocks } from './blocks'
import { tinyMceBoxColumns, type EditorTextBoxTinyMceItem } from './boxColumns'
import { parseTinyMceHtml } from './parse'
import {
  EditorTextBoxConversionError,
  editorTextBoxConversionReportCollector,
  type EditorTextBoxConversionReport,
} from './report'
import { tinyMceHighlightCount } from './styleFold'

export type { EditorTextBoxTinyMceItem } from './boxColumns'
export { EditorTextBoxConversionError } from './report'
export type {
  EditorTextBoxConversionDroppedEdit,
  EditorTextBoxConversionErrorReason,
  EditorTextBoxConversionReport,
  EditorTextBoxConversionReportEntry,
} from './report'

/** A converted TinyMCE item: the Lotus box to write, and what did not carry. */
export type EditorTextBoxConversion = {
  box: EditorTextBoxInput
  report: EditorTextBoxConversionReport
}

/**
 * The limits the row store's input schema enforces that the converter can
 * check here, so a box it returns is one the save route accepts: characters
 * per run, runs per block, blocks per box, and every field run's key and
 * params.
 */
const refuseOverLimits = (box: EditorTextBoxInput): void => {
  if (box.blocks.length > EDITOR_MAX_BLOCKS_PER_BOX)
    throw new EditorTextBoxConversionError(
      'block-count',
      `the converted box holds ${box.blocks.length} blocks; a box holds at most ${EDITOR_MAX_BLOCKS_PER_BOX}`,
    )
  box.blocks.forEach((block, blockIndex) => {
    if (block.runs.length > EDITOR_MAX_RUNS_PER_BLOCK)
      throw new EditorTextBoxConversionError(
        'run-count',
        `block ${blockIndex} holds ${block.runs.length} runs; a block holds at most ${EDITOR_MAX_RUNS_PER_BLOCK}`,
      )
    for (const run of block.runs) {
      if (
        run.kind === 'text' &&
        run.characters.normalize('NFC').length > EDITOR_MAX_RUN_CHARACTERS
      )
        throw new EditorTextBoxConversionError(
          'run-length',
          `block ${blockIndex} holds a run of more than ${EDITOR_MAX_RUN_CHARACTERS} characters`,
        )
      if (run.kind === 'field') {
        const refusal = validateEditorFieldParams(run.fieldKey, run.params)
        if (refusal !== undefined)
          throw new EditorTextBoxConversionError(
            'field',
            `block ${blockIndex} holds a field run that ${refusal}`,
          )
      }
    }
  })
}

const familyOf = (
  styling: EditorCharacterStylingSpec | undefined,
): string | undefined =>
  styling !== undefined && 'fontFamily' in styling
    ? styling.fontFamily
    : undefined

/** Every family the box's text is set in, each once, in order. */
const familiesOf = (box: EditorTextBoxInput): string[] => {
  const families = new Set<string>()
  for (const block of box.blocks)
    for (const styling of [
      block.characterStyling,
      ...block.runs.map((run) => run.styling),
    ]) {
      const family = familyOf(styling)
      if (family !== undefined) families.add(family)
    }
  return [...families]
}

/**
 * A TinyMCE text item as a Lotus text box input and the report of what did
 * not carry. Pure: it reads the item's stored HTML, geometry, state and
 * edits, and no case data. An item the row store could not take as a box
 * raises `EditorTextBoxConversionError` before anything is returned.
 */
export const editorTextBoxFromTinyMceItem = (
  item: EditorTextBoxTinyMceItem,
): EditorTextBoxConversion => {
  const report = editorTextBoxConversionReportCollector()
  const columns = tinyMceBoxColumns(item, report)
  const body = parseTinyMceHtml(
    typeof item.text?.value === 'string' ? item.text.value : '',
  )
  const highlights = tinyMceHighlightCount(body)
  if (highlights > 0)
    report.add({ kind: 'highlight-dropped', count: highlights })
  const blocks = tinyMceBlocks(body, report)
  const box: EditorTextBoxInput = {
    ...columns,
    blocks: blocks.length > 0 ? blocks : [{ kind: 'paragraph', runs: [] }],
  }
  refuseOverLimits(box)
  if (isEditorTextBoxPlaceholderOnly(box))
    report.add({ kind: 'placeholder-only' })
  for (const family of familiesOf(box))
    if (canonicalFontName(family) === undefined)
      report.add({ kind: 'family-outside-picker', family })
  return { box, report: report.entries() }
}
